/**
 * Tarefas e follow-ups comerciais (Task 13, brief 3.4).
 *
 * Regras que esta tela respeita:
 *
 * 1. **A fila é a mesma em todo lugar.** A ordenação (atrasadas primeiro,
 *    depois o que vence hoje, depois o resto) vem de `taskQueue` em
 *    `crm/dataset.ts` — a MESMA que a visão 360 do cliente usa, para o operador
 *    não ver duas prioridades para a mesma tarefa.
 * 2. **Fechar exige dizer o que aconteceu.** O formulário pede o resultado e o
 *    servidor o exige: follow-up fechado sem registro apaga a única informação
 *    que a próxima pessoa precisava (D-13.10).
 * 3. **Cada linha navega até o cliente e até a negociação** que a motivou.
 *
 * Redesign R6 (apresentação apenas): wrapper e header no padrão aprovado; a
 * fila em RecordTable migrado; a situação do prazo (atrasada/hoje/a vencer/
 * encerrada) vira SELO ícone+texto tokenizado (nunca só cor — WCAG 1.4.1); o
 * formulário de fechamento usa campos/botões tokenizados; o histórico vira
 * cartão. Todos os `data-*` (incl. `data-page`, `data-task-due`,
 * `data-task-close`, `data-task-outcome`, `data-selected-task`,
 * `TASK_KIND_LABELS`), os textos e os links são PRESERVADOS.
 *
 * Migrado para `.tsx`/JSX real (Cluster C — Atendimento & CRM, redesign por
 * perfil; ver `docs/architecture/ADR-0011-*.md`). Mudança de sintaxe apenas
 * (`createElement` → JSX): os ícones (`AlarmClock`, `CheckCircle2`, `Clock`)
 * já existiam.
 */
// Import explícito de `React` — ver o mesmo comentário em `AppShell.tsx`.
import React, { type FormEvent, type ReactElement } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { AlarmClock, CheckCircle2, Clock, type LucideIcon } from "lucide-react";
import { RecordTable } from "../components/RecordTable.js";
import { CrmBoundary } from "../components/CrmBoundary.js";
import { StatusPanel } from "../components/StatusPanel.js";
import { Badge, EmptyState } from "../ui/index.js";
import { isCrmSourceAwaitingContract, taskQueue, type CrmDataset, type TaskQueueRow } from "../crm/dataset.js";
import { TASK_KIND_LABELS } from "../crm/pipeline.js";
import { useCrmTaskClose } from "../queries.js";
import { AWAITING_CONTRACT_LABEL } from "../dashboard/kpis.js";
import { formatDateTime } from "../format.js";
import type { TarefasViewProps, TaskCloseHandle } from "../crm/view-props.js";
import type { CrmTask } from "../api/types.js";

const DUE_LABELS: Record<TaskQueueRow["due"], string> = {
  overdue: "atrasada",
  today: "vence hoje",
  upcoming: "a vencer",
  closed: "encerrada"
};

// Situação do prazo como SELO ícone+texto (WCAG 1.4.1: a cor é reforço, o texto
// e o ícone carregam a informação). O tom segue tokens — atrasada = danger,
// vence hoje = warning, a vencer/encerrada = neutro.
const DUE_TONE: Record<TaskQueueRow["due"], string> = {
  overdue: "border-danger text-danger",
  today: "border-warning text-warning",
  upcoming: "border-border-strong text-body",
  closed: "border-border-strong text-muted"
};

const DUE_ICON: Record<TaskQueueRow["due"], LucideIcon> = {
  overdue: AlarmClock,
  today: Clock,
  upcoming: Clock,
  closed: CheckCircle2
};

/** Selo ícone+texto da situação do prazo. `data-task-due` fica no wrapper. */
function DueBadge({ due }: { due: TaskQueueRow["due"] }): ReactElement {
  const Icon = DUE_ICON[due];
  return (
    <span
      className={`inline-flex items-center gap-1.5 rounded-pill border bg-surface px-2 py-0.5 text-xs font-semibold ${DUE_TONE[due]}`}
      data-task-due={due}
    >
      <Icon className="h-3.5 w-3.5 shrink-0" aria-hidden focusable={false} />
      <span>{DUE_LABELS[due]}</span>
    </span>
  );
}

/** Só o que o formulário precisa do `FormData` — torna o submit testável. */
export interface TaskCloseFormValues {
  get(name: string): unknown;
}

export type TaskCloseSubmission =
  | { ok: true; input: { taskId: string; status: "done" | "cancelled"; outcome: string } }
  | { ok: false; error: "outcome_required" };

/**
 * O caminho de submit REAL do fechamento, isolado do evento de DOM (que não
 * existe sem navegador): `onSubmit` abaixo só traduz o evento para cá.
 *
 * O desfecho (`done`/`cancelled`) é um campo do formulário e não o botão que
 * disparou o submit, de propósito: ler `event.submitter` amarraria esta regra a
 * um detalhe do DOM que o teste não tem como exercitar sem navegador.
 *
 * A recusa local existe para avisar na hora; a autoridade continua sendo o
 * servidor, que responde 422 se o resultado vier vazio.
 */
export function submitTaskClose(task: CrmTask, values: TaskCloseFormValues, close: TaskCloseHandle): TaskCloseSubmission {
  const rawOutcome = values.get("outcome");
  const outcome = typeof rawOutcome === "string" ? rawOutcome.trim() : "";
  if (!outcome) return { ok: false, error: "outcome_required" };
  const rawStatus = values.get("status");
  const status: "done" | "cancelled" = rawStatus === "cancelled" ? "cancelled" : "done";
  const input = { taskId: task.id, status, outcome };
  close.submit(input);
  return { ok: true, input };
}

function TaskCloseForm({ task, close }: { task: CrmTask; close: TaskCloseHandle }): ReactElement {
  function onSubmit(event: FormEvent<HTMLFormElement>): void {
    event.preventDefault();
    submitTaskClose(task, new FormData(event.currentTarget), close);
  }

  const outcomeId = `task-outcome-${task.id}`;
  const statusId = `task-status-${task.id}`;
  const controlClass =
    "min-h-[40px] rounded-control border border-border-strong bg-surface px-3 text-sm text-ink hover:border-ink focus-visible:outline-none disabled:opacity-60";
  return (
    <form className="task-close flex flex-wrap items-end gap-3 border-t border-border pt-3" data-task-close={task.id} onSubmit={onSubmit}>
      <div className="flex min-w-[14rem] flex-1 flex-col gap-1">
        <label htmlFor={outcomeId} className="text-xs font-extrabold text-muted">
          Resultado
        </label>
        <input id={outcomeId} name="outcome" type="text" required disabled={close.pending} data-task-field="outcome" className={controlClass} placeholder="O que aconteceu?" />
      </div>
      <div className="flex flex-col gap-1">
        <label htmlFor={statusId} className="text-xs font-extrabold text-muted">
          Desfecho
        </label>
        <select id={statusId} name="status" defaultValue="done" disabled={close.pending} data-task-field="status" className={controlClass}>
          <option value="done">Concluída</option>
          <option value="cancelled">Cancelada</option>
        </select>
      </div>
      <button
        type="submit"
        disabled={close.pending}
        data-action={`task-close-${task.id}`}
        className="inline-flex min-h-[40px] items-center justify-center rounded-control bg-brand px-4 text-sm font-bold text-on-brand shadow-s1 transition-colors duration-fast ease-s2 hover:bg-brand-pressed focus-visible:outline-none disabled:opacity-60"
      >
        {close.pending ? "Registrando…" : "Registrar fechamento"}
      </button>
    </form>
  );
}

export function TarefasView({ crm, now, ownerFilter = null, taskId = null, close }: TarefasViewProps): ReactElement {
  if (isCrmSourceAwaitingContract(crm, "tasks")) {
    return (
      <div className="page page--tarefas flex flex-col gap-6" data-page="tarefas">
        <header className="drilldown-header flex flex-col gap-1">
          <h2 className="text-2xl font-black text-ink sm:text-3xl">Tarefas e follow-ups</h2>
        </header>
        <StatusPanel
          kind="awaiting_source"
          title={`Tarefas ${AWAITING_CONTRACT_LABEL}`}
          detail="A rota /api/crm/tasks não respondeu neste ambiente. Fila vazia aqui diria que a operação não tem nenhum follow-up pendente."
        />
      </div>
    );
  }
  const instant = now ?? new Date();
  const queue = taskQueue(crm, instant).filter((row) => ownerFilter === null || row.task.ownerProfileId === ownerFilter);
  const overdue = queue.filter((row) => row.due === "overdue").length;
  const today = queue.filter((row) => row.due === "today").length;
  const customerNames = new Map(crm.customers.map((customer) => [customer.id, customer.name]));
  const opportunityTitles = new Map(crm.opportunities.map((opportunity) => [opportunity.id, opportunity.title]));
  const closed = crm.tasks
    .filter((task) => task.status !== "open" && (ownerFilter === null || task.ownerProfileId === ownerFilter))
    .sort((a, b) => (b.closedAt ?? "").localeCompare(a.closedAt ?? ""));
  // Tarefa exata pedida pelo drill-down do 360 (`?task=`). Ela pode estar na
  // fila (aberta) ou no histórico (já fechada) — as duas pontas marcam.
  const selectedQueued = taskId === null ? null : (queue.find((row) => row.task.id === taskId) ?? null);
  const selectedClosed = taskId === null ? null : (closed.find((task) => task.id === taskId) ?? null);
  return (
    <div className="page page--tarefas flex flex-col gap-6" data-page="tarefas" {...(taskId ? { "data-selected-task": taskId } : {})}>
      <header className="drilldown-header flex flex-wrap items-end justify-between gap-3">
        <div className="min-w-0">
          <h2 className="text-2xl font-black text-ink sm:text-3xl">Tarefas e follow-ups</h2>
          <p className="drilldown-header__origin mt-1 text-base text-muted" data-task-queue={queue.length} data-task-overdue={overdue}>
            {`${queue.length} tarefa(s) em aberto, ${overdue} atrasada(s).`}
          </p>
        </div>
        {ownerFilter ? (
          <Link to="/crm/tarefas" className="drilldown-header__back text-sm font-bold text-brand-text underline">
            Ver a fila completa
          </Link>
        ) : null}
      </header>
      <dl aria-label="Resumo da fila" className="m-0 flex flex-wrap gap-x-10 gap-y-2 rounded-panel border border-border bg-surface px-5 py-3 shadow-s1">
        <div className="flex items-baseline gap-2">
          <dt className="text-sm font-extrabold text-muted">Atrasadas</dt>
          <dd className={"m-0 text-xl font-black tabular-nums " + (overdue > 0 ? "text-danger" : "text-ink")}>{overdue}</dd>
        </div>
        <div className="flex items-baseline gap-2">
          <dt className="text-sm font-extrabold text-muted">Vencem hoje</dt>
          <dd className="m-0 text-xl font-black tabular-nums text-ink">{today}</dd>
        </div>
        <div className="flex items-baseline gap-2">
          <dt className="text-sm font-extrabold text-muted">Em aberto</dt>
          <dd className="m-0 text-xl font-black tabular-nums text-ink">{queue.length}</dd>
        </div>
      </dl>
      {ownerFilter ? (
        <p className="page__note text-sm text-muted" data-owner-scope={ownerFilter}>
          {`Recorte do responsável ${ownerFilter}.`}
        </p>
      ) : null}
      {taskId ? (
        selectedQueued || selectedClosed ? (
          <p className="page__note text-sm text-muted" data-task-selected={taskId}>
            {selectedQueued
              ? `Tarefa ${selectedQueued.task.title} destacada na fila — é o registro de onde você veio.`
              : `Tarefa ${selectedClosed?.title ?? taskId} já encerrada: destacada no histórico abaixo.`}
          </p>
        ) : (
          <StatusPanel
            kind="empty"
            title={`A tarefa ${taskId} não está neste recorte`}
            detail="O link pediu uma tarefa que não aparece nem na fila nem no histórico deste recorte (o responsável pode ter mudado). Nada abaixo está destacado."
          />
        )
      ) : null}
      <p className="page__note m-0 text-xs text-muted">
        Prazo e responsável são obrigatórios no registro, e concluir/cancelar exige dizer o resultado — tarefa
        fechada em branco apaga o que a próxima pessoa precisava saber.
      </p>
      {queue.length === 0 ? (
        <EmptyState title="Nenhuma tarefa em aberto neste recorte" description="Quando houver follow-up pendente, ele aparece aqui, atrasadas primeiro." />
      ) : (
        <ul className="m-0 flex list-none flex-col gap-3 p-0" aria-label="Fila de follow-up: atrasadas primeiro, depois o que vence hoje">
          {queue.map((row) => {
            const selectedRow = selectedQueued?.task.id === row.task.id;
            return (
              <li
                key={row.task.id}
                data-record-id={row.task.id}
                {...(selectedRow ? { "data-selected": "true", "aria-current": "true" as const } : {})}
                className={
                  "flex flex-col gap-3 rounded-panel border border-l-4 bg-surface p-4 shadow-s1 " +
                  (row.due === "overdue" ? "border-l-danger" : row.due === "today" ? "border-l-warning" : "border-l-info") +
                  (selectedRow ? " border-brand ring-1 ring-brand" : " border-border")
                }
              >
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div className="flex min-w-0 flex-col gap-1">
                    <p className="m-0 text-base font-extrabold text-ink">{row.task.title}</p>
                    <p className="m-0 flex flex-wrap items-center gap-x-3 gap-y-1 text-sm text-muted">
                      <Badge tone="neutral">{TASK_KIND_LABELS[row.task.kind]}</Badge>
                      <Link to={`/clientes/${row.task.customerId}`} className="font-bold text-ink hover:underline">
                        {customerNames.get(row.task.customerId) ?? row.task.customerId}
                      </Link>
                      {/* O destino é a NEGOCIAÇÃO vinculada, não a fila do responsável: o recorte por dono
                          abriria outras negociações e perderia a que motivou a tarefa. */}
                      {row.task.opportunityId ? (
                        <Link to={`/crm/pipeline?opportunity=${encodeURIComponent(row.task.opportunityId)}`} className="hover:underline">
                          {opportunityTitles.get(row.task.opportunityId) ?? row.task.opportunityId}
                        </Link>
                      ) : (
                        <span>sem negociação vinculada</span>
                      )}
                      <span data-task-owner={row.task.ownerProfileId}>{row.task.ownerLabel}</span>
                    </p>
                  </div>
                  <div className="flex flex-col items-end gap-1">
                    <DueBadge due={row.due} />
                    <span className="text-xs text-muted">{`Prazo ${formatDateTime(row.task.dueAt)}`}</span>
                  </div>
                </div>
                {close ? (
                  <TaskCloseForm task={row.task} close={close} />
                ) : (
                  <span className="text-xs text-muted" data-task-close="readonly">
                    gravação não montada nesta visualização
                  </span>
                )}
              </li>
            );
          })}
        </ul>
      )}
      {close?.error ? <StatusPanel kind="error" title="A tarefa não foi fechada" detail={close.error.message} /> : null}
      {close?.closedTaskId ? <StatusPanel kind="empty" title={`Tarefa ${close.closedTaskId} fechada com resultado registrado.`} /> : null}
      <section className="task-history flex flex-col gap-3 rounded-panel border border-border bg-surface p-5 shadow-s1" aria-label="Tarefas encerradas">
        <h3 className="text-lg font-extrabold text-ink">Encerradas</h3>
        <RecordTable<CrmTask>
          bare
          caption="Tarefas concluídas ou canceladas, com o resultado registrado"
          rows={closed}
          idOf={(task) => task.id}
          emptyMessage="Nenhuma tarefa encerrada neste recorte."
          selectedId={selectedClosed?.id ?? null}
          animateRows
          columns={[
            { key: "title", label: "Tarefa", render: (task) => task.title },
            {
              key: "customer",
              label: "Cliente",
              render: (task) => <Link to={`/clientes/${task.customerId}`}>{customerNames.get(task.customerId) ?? task.customerId}</Link>
            },
            { key: "status", label: "Situação", render: (task) => (task.status === "done" ? "concluída" : "cancelada") },
            {
              key: "outcome",
              label: "Resultado",
              render: (task) => <span data-task-outcome={task.outcome}>{task.outcome || "sem registro"}</span>
            },
            {
              key: "closedAt",
              label: "Fechada em",
              render: (task) => (task.closedAt ? formatDateTime(task.closedAt) : "—")
            }
          ]}
        />
      </section>
    </div>
  );
}

export function Tarefas(): ReactElement {
  const [searchParams] = useSearchParams();
  const ownerFilter = searchParams.get("owner");
  // `task=` é o id da tarefa de onde o operador veio (drill-down do 360).
  const taskId = searchParams.get("task");
  const mutation = useCrmTaskClose();
  const close: TaskCloseHandle = {
    submit: (input) => mutation.mutate(input),
    pending: mutation.isPending,
    error: mutation.error ?? null,
    closedTaskId: mutation.data?.id ?? null
  };
  return <CrmBoundary>{(crm: CrmDataset) => <TarefasView crm={crm} ownerFilter={ownerFilter} taskId={taskId} close={close} />}</CrmBoundary>;
}
